import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { LogOut, User, Lock, Calendar, Trash2, Pencil, Plus, Check, Camera, Loader2, ShieldCheck, X } from "lucide-react";
import { api, imageSourceFor, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PrimaryButton, GhostButton, Field, Card, SectionLabel } from "@/components/mo";

/* -------------------- Auth (logged-out) view -------------------- */
function AuthView() {
  const { login, register, adminLogin } = useAuth();
  const [mode, setMode] = useState("login"); // login | register | admin
  const [form, setForm] = useState({ name: "", email: "", password: "", username: "" });
  const [loading, setLoading] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const guard = mode === "admin" ? { readOnly: !unlocked, onFocus: () => setUnlocked(true) } : {};
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await login({ email: form.email, password: form.password });
      else if (mode === "register") await register({ name: form.name, email: form.email, password: form.password });
      else await adminLogin({ username: form.username, password: form.password });
      toast.success(mode === "admin" ? "Benvenuto, amministratore" : "Accesso effettuato");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Errore");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-md px-5 pt-10">
      <div className="mb-6 flex flex-col items-center gap-2 text-center">
        <div className="flex h-20 w-20 items-center justify-center rounded-full border-2 border-mo-brand bg-mo-surface2">
          <User className="h-9 w-9 text-mo-brand" />
        </div>
        <h1 className="font-display text-3xl font-bold text-mo-ink">
          {mode === "admin" ? "Area Amministratore" : mode === "register" ? "Crea account" : "Bentornato"}
        </h1>
        <p className="font-body text-[13px] text-mo-muted">Mörphösy Tattoo Art & Studio</p>
      </div>

      {mode !== "admin" && (
        <div className="mb-4 flex gap-2 rounded-full border border-mo-line bg-mo-surface2 p-1">
          {[["login", "Accedi"], ["register", "Registrati"]].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setMode(k)}
              data-testid={`auth-tab-${k}`}
              className={"flex-1 rounded-full py-2 font-body text-[13px] font-bold transition-colors " + (mode === k ? "bg-mo-brand text-mo-onbrand" : "text-mo-muted")}
            >
              {l}
            </button>
          ))}
        </div>
      )}

      <Card>
        <form onSubmit={submit} autoComplete={mode === "admin" ? "off" : "on"} className="flex flex-col gap-3" key={mode}>
          {mode === "register" && <Field label="Nome" value={form.name} onChange={set("name")} placeholder="Il tuo nome" data-testid="reg-name" />}
          {mode === "admin" ? (
            <Field label="Nome utente" value={form.username} onChange={set("username")} placeholder="Nome utente" autoComplete="off" name="mo-admin-user" {...guard} data-testid="admin-username" />
          ) : (
            <Field label="Email" type="email" value={form.email} onChange={set("email")} placeholder="tu@email.com" autoComplete="email" data-testid="auth-email" />
          )}
          <Field label="Password" type="password" value={form.password} onChange={set("password")} placeholder="••••••" autoComplete={mode === "admin" ? "new-password" : mode === "register" ? "new-password" : "current-password"} name={mode === "admin" ? "mo-admin-pass" : "password"} {...guard} data-testid="auth-password" />
          <PrimaryButton type="submit" loading={loading} icon={mode === "admin" ? ShieldCheck : null} className="mt-1 w-full" data-testid="auth-submit">
            {mode === "admin" ? "Accedi come admin" : mode === "register" ? "Registrati" : "Accedi"}
          </PrimaryButton>
        </form>
      </Card>

      <button
        onClick={() => {
          setMode(mode === "admin" ? "login" : "admin");
          setForm({ name: "", email: "", password: "", username: "" });
          setUnlocked(false);
        }}
        data-testid="toggle-admin"
        className="mx-auto mt-5 flex items-center gap-2 font-body text-xs font-bold text-mo-muted hover:text-mo-brand"
      >
        <Lock className="h-3.5 w-3.5" />
        {mode === "admin" ? "Torna all'accesso utenti" : "Accedi come amministratore"}
      </button>
    </div>
  );
}

/* -------------------- Shared bits -------------------- */
const MAX_WORKS = 5;

function UploadTile({ item, onUploaded, onRemove, label = "Carica", className = "h-24 w-24", testid }) {
  const [uploading, setUploading] = useState(false);
  const src = imageSourceFor(item);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const res = await api.uploadImage(file);
      onUploaded(res.path);
    } catch {
      toast.error("Caricamento non riuscito");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className={"relative flex-shrink-0 " + className}>
      <label data-testid={testid} className="relative flex h-full w-full cursor-pointer items-center justify-center overflow-hidden rounded-xl border border-dashed border-mo-line bg-mo-input">
        {src ? <img src={src} alt="" className="h-full w-full object-cover" /> : (
          <span className="flex flex-col items-center gap-1 text-mo-muted"><Camera className="h-5 w-5" /><span className="font-body text-[11px]">{label}</span></span>
        )}
        {uploading && <div className="absolute inset-0 flex items-center justify-center bg-black/50"><Loader2 className="h-5 w-5 animate-spin text-mo-brand" /></div>}
        <input type="file" accept="image/*" className="hidden" onChange={pick} />
      </label>
      {onRemove && (
        <button type="button" onClick={onRemove} data-testid={testid ? `${testid}-remove` : undefined} className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-mo-error text-white">
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

function MockupThumb({ path }) {
  const [broken, setBroken] = useState(false);
  if (!path || broken) return null;
  const src = imageSourceFor({ imagePath: path });
  return (
    <a href={src} target="_blank" rel="noreferrer" data-testid="appt-mockup" title="Mockup">
      <img src={src} alt="Mockup" onError={() => setBroken(true)} className="h-12 w-12 rounded-lg border border-mo-line object-cover" />
    </a>
  );
}

/* -------------------- Regular user view -------------------- */
function statusColor(s) {
  const t = (s || "").toLowerCase();
  if (t.includes("conferm")) return "text-mo-success";
  if (t.includes("annull")) return "text-mo-error";
  return "text-mo-brand";
}

function UserView() {
  const { user, logout } = useAuth();
  const qc = useQueryClient();
  const { data: appts = [], isLoading } = useQuery({ queryKey: ["my-appts"], queryFn: api.myAppointments });

  const remove = async (id) => {
    try {
      await api.deleteAppointment(id);
      qc.invalidateQueries({ queryKey: ["my-appts"] });
      toast.success("Appuntamento rimosso");
    } catch {
      toast.error("Errore");
    }
  };

  return (
    <div className="px-5 pt-8 md:px-10">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-mo-brand bg-mo-surface2">
            <User className="h-7 w-7 text-mo-brand" />
          </div>
          <div>
            <h1 className="font-display text-3xl font-bold text-mo-ink">{user.name || "Il mio profilo"}</h1>
            <p className="font-body text-[13px] text-mo-muted">{user.email}</p>
          </div>
        </div>
        <GhostButton icon={LogOut} onClick={logout} data-testid="user-logout">Esci</GhostButton>
      </div>

      <SectionLabel>I miei appuntamenti</SectionLabel>
      <div className="mt-3 flex flex-col gap-3">
        {isLoading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-mo-brand" /></div>
        ) : appts.length === 0 ? (
          <Card><p className="font-body text-sm text-mo-muted">Non hai ancora appuntamenti. Vai su Prenota per richiederne uno.</p></Card>
        ) : (
          appts.map((a) => (
            <div key={a.id} data-testid={`my-appt-${a.id}`} className="flex items-center gap-4 rounded-xl border border-mo-line bg-mo-card p-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-mo-surface3">
                <Calendar className="h-5 w-5 text-mo-brand" />
              </div>
              <div className="flex-1">
                <p className="font-body text-[15px] font-bold text-mo-ink">{a.date}</p>
                <p className="font-body text-xs text-mo-muted line-clamp-2">{a.description || "Richiesta appuntamento"}</p>
                <span className={"font-body text-[11px] font-bold " + statusColor(a.status)}>{a.status}</span>
              </div>
              <MockupThumb path={a.mockupPath} />
              <button onClick={() => remove(a.id)} data-testid={`del-my-appt-${a.id}`} className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-mo-surface3">
                <Trash2 className="h-4 w-4 text-mo-error" />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/* -------------------- Admin: entity editor -------------------- */
const EMPTY = { open: false, kind: "style", id: null, name: "", secondary: "", imagePath: null, imageUrl: null, works: [], uploading: false };

function AdminView() {
  const { logout } = useAuth();
  const qc = useQueryClient();
  const [section, setSection] = useState("studio");
  const [editor, setEditor] = useState(EMPTY);

  const { data: artists = [] } = useQuery({ queryKey: ["artists"], queryFn: api.getArtists });
  const { data: styles = [] } = useQuery({ queryKey: ["styles"], queryFn: api.getStyles });
  const { data: appts = [] } = useQuery({ queryKey: ["all-appts"], queryFn: api.allAppointments, enabled: section === "appuntamenti" });
  const { data: studio } = useQuery({ queryKey: ["studio"], queryFn: api.getStudio });

  const invalidate = (k) => qc.invalidateQueries({ queryKey: [k] });

  const saveEditor = async () => {
    if (!editor.name.trim()) return toast.error("Inserisci un nome");
    try {
      const body = { id: editor.id, name: editor.name.trim(), imagePath: editor.imagePath, imageUrl: editor.imageUrl };
      if (editor.kind === "artist") await api.saveArtist({ ...body, specialty: editor.secondary.trim(), works: editor.works });
      else await api.saveStyle({ ...body, description: editor.secondary.trim() });
      invalidate(editor.kind === "artist" ? "artists" : "styles");
      setEditor(EMPTY);
      toast.success("Salvato");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const removeEntity = async (kind, item) => {
    if (!window.confirm(`Eliminare "${item.name}"?`)) return;
    try {
      if (kind === "artist") await api.deleteArtist(item.id);
      else await api.deleteStyle(item.id);
      invalidate(kind === "artist" ? "artists" : "styles");
      toast.success("Eliminato");
    } catch {
      toast.error("Errore");
    }
  };

  const removeAppt = async (id) => {
    if (!window.confirm("Eliminare la richiesta?")) return;
    try {
      await api.deleteAppointment(id);
      invalidate("all-appts");
      toast.success("Rimosso");
    } catch {
      toast.error("Errore");
    }
  };

  const pickEditorImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setEditor((ed) => ({ ...ed, uploading: true }));
    try {
      const res = await api.uploadImage(file);
      setEditor((ed) => ({ ...ed, imagePath: res.path, imageUrl: null, uploading: false }));
      toast.success("Immagine caricata");
    } catch {
      setEditor((ed) => ({ ...ed, uploading: false }));
      toast.error("Caricamento non riuscito");
    }
  };

  const list = section === "artisti" ? artists : section === "stili" ? styles : [];
  const SECTIONS = [["studio", "Studio"], ["artisti", "Artisti"], ["stili", "Stili"], ["appuntamenti", "Appuntamenti"]];

  return (
    <div className="px-5 pt-8 md:px-10">
      <div className="mb-5 flex items-end justify-between">
        <div>
          <SectionLabel>Amministratore</SectionLabel>
          <h1 className="font-display text-3xl font-bold text-mo-ink">Gestione Studio</h1>
        </div>
        <GhostButton icon={LogOut} onClick={logout} data-testid="admin-logout">Esci</GhostButton>
      </div>

      <div className="no-scrollbar mb-5 flex gap-2 overflow-x-auto">
        {SECTIONS.map(([k, l]) => (
          <button
            key={k}
            onClick={() => setSection(k)}
            data-testid={`seg-${k}`}
            className={"flex-shrink-0 rounded-lg border px-4 py-2 font-body text-[13px] font-bold transition-colors " + (section === k ? "border-mo-brand bg-mo-brand text-mo-onbrand" : "border-mo-line bg-mo-surface2 text-mo-muted")}
          >
            {l}
          </button>
        ))}
      </div>

      {section === "studio" && <StudioEditor studio={studio} onSaved={() => invalidate("studio")} />}

      {(section === "artisti" || section === "stili") && (
        <div className="flex flex-col gap-3">
          <PrimaryButton icon={Plus} onClick={() => setEditor({ ...EMPTY, open: true, kind: section === "artisti" ? "artist" : "style" })} data-testid="admin-add-entity">
            {section === "artisti" ? "Aggiungi artista" : "Aggiungi stile"}
          </PrimaryButton>
          {list.map((item) => {
            const src = imageSourceFor(item);
            const kind = section === "artisti" ? "artist" : "style";
            return (
              <div key={item.id} data-testid={`entity-${item.id}`} className="flex items-center gap-4 rounded-xl border border-mo-line bg-mo-card p-3">
                {src ? <img src={src} alt="" className="h-14 w-14 rounded-lg object-cover" /> : <div className="h-14 w-14 rounded-lg bg-mo-surface3" />}
                <div className="flex-1">
                  <p className="font-body text-[15px] font-bold text-mo-ink">{item.name}</p>
                  <p className="font-body text-xs text-mo-muted">{section === "artisti" ? item.specialty : item.description}</p>
                </div>
                <button onClick={() => setEditor({ open: true, kind, id: item.id, name: item.name, secondary: kind === "artist" ? item.specialty : item.description, imagePath: item.imagePath, imageUrl: item.imageUrl, works: item.works || [], uploading: false })} data-testid={`edit-${item.id}`} className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-mo-surface3">
                  <Pencil className="h-4 w-4 text-mo-ink" />
                </button>
                <button onClick={() => removeEntity(kind, item)} data-testid={`del-${item.id}`} className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-mo-surface3">
                  <Trash2 className="h-4 w-4 text-mo-error" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {section === "appuntamenti" && (
        <div className="flex flex-col gap-3">
          {appts.length === 0 ? (
            <Card><p className="font-body text-sm text-mo-muted">Nessuna richiesta al momento.</p></Card>
          ) : (
            appts.map((a) => (
              <div key={a.id} data-testid={`appt-${a.id}`} className="flex items-center gap-4 rounded-xl border border-mo-line bg-mo-card p-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-mo-surface3">
                  <Calendar className="h-5 w-5 text-mo-brand" />
                </div>
                <div className="flex-1">
                  <p className="font-body text-[15px] font-bold text-mo-ink">{a.clientName}</p>
                  <p className="font-body text-xs text-mo-muted line-clamp-2">{a.date} · {a.status}{a.contactInfo ? ` · ${a.contactInfo}` : ""}</p>
                  {a.userEmail ? <p className="font-body text-[11px] text-mo-brand">{a.userEmail}</p> : <p className="font-body text-[11px] text-mo-muted">Ospite (non registrato)</p>}
                </div>
                <MockupThumb path={a.mockupPath} />
                <button onClick={() => removeAppt(a.id)} data-testid={`del-appt-${a.id}`} className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-mo-surface3">
                  <Trash2 className="h-4 w-4 text-mo-error" />
                </button>
              </div>
            ))
          )}
        </div>
      )}

      {/* Entity editor modal */}
      {editor.open && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 md:items-center md:p-4" onClick={() => setEditor(EMPTY)}>
          <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-t-3xl border border-mo-line bg-mo-surface2 p-6 md:rounded-3xl" onClick={(e) => e.stopPropagation()} data-testid="entity-editor">
            <h3 className="mb-4 font-display text-2xl font-bold text-mo-ink">
              {editor.id ? "Modifica" : "Nuovo"} {editor.kind === "artist" ? "artista" : "stile"}
            </h3>
            <label className="relative mb-4 flex h-36 cursor-pointer items-center justify-center overflow-hidden rounded-xl border border-dashed border-mo-line bg-mo-input" data-testid="editor-pick-image">
              {editor.imagePath || editor.imageUrl ? (
                <img src={imageSourceFor(editor)} alt="" className="h-full w-full object-cover" />
              ) : (
                <span className="flex flex-col items-center gap-1 text-mo-muted"><Camera className="h-6 w-6" /><span className="font-body text-xs">Carica foto</span></span>
              )}
              {editor.uploading && <div className="absolute inset-0 flex items-center justify-center bg-black/50"><Loader2 className="h-5 w-5 animate-spin text-mo-brand" /></div>}
              <input type="file" accept="image/*" className="hidden" onChange={pickEditorImage} />
            </label>
            <Field label="Nome" value={editor.name} onChange={(e) => setEditor((ed) => ({ ...ed, name: e.target.value }))} placeholder={editor.kind === "artist" ? "Es. Carlo" : "Es. Blackwork"} data-testid="editor-name" />
            <div className="mt-3">
              <Field label={editor.kind === "artist" ? "Specializzazione" : "Descrizione"} value={editor.secondary} onChange={(e) => setEditor((ed) => ({ ...ed, secondary: e.target.value }))} placeholder={editor.kind === "artist" ? "Es. Realistico" : "Es. Neri pieni"} data-testid="editor-secondary" />
            </div>
            {editor.kind === "artist" && (
              <div className="mt-4" data-testid="editor-works">
                <p className="mb-2 font-body text-xs font-bold text-mo-muted">Lavori effettuati ({editor.works.length}/{MAX_WORKS})</p>
                <div className="flex flex-wrap gap-3 pt-2">
                  {editor.works.map((w, i) => (
                    <UploadTile
                      key={i}
                      item={w}
                      className="h-20 w-20"
                      testid={`editor-work-${i}`}
                      onUploaded={(path) => setEditor((ed) => ({ ...ed, works: ed.works.map((x, j) => (j === i ? { imagePath: path, imageUrl: null } : x)) }))}
                      onRemove={() => setEditor((ed) => ({ ...ed, works: ed.works.filter((_, j) => j !== i) }))}
                    />
                  ))}
                  {editor.works.length < MAX_WORKS && (
                    <UploadTile
                      item={null}
                      label="Aggiungi"
                      className="h-20 w-20"
                      testid="editor-work-add"
                      onUploaded={(path) => setEditor((ed) => ({ ...ed, works: [...ed.works, { imagePath: path, imageUrl: null }].slice(0, MAX_WORKS) }))}
                    />
                  )}
                </div>
              </div>
            )}
            <div className="mt-5 flex gap-2">
              <GhostButton onClick={() => setEditor(EMPTY)} className="flex-1">Annulla</GhostButton>
              <PrimaryButton icon={Check} onClick={saveEditor} className="flex-1" data-testid="editor-save">Salva</PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StudioEditor({ studio, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  React.useEffect(() => {
    if (studio && !form) setForm({ ...studio });
  }, [studio, form]);

  if (!form) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-mo-brand" /></div>;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setFlash = (i, patch) => setForm((f) => ({ ...f, flashes: f.flashes.map((x, j) => (j === i ? { ...x, ...patch } : x)) }));

  const uploadHero = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await api.uploadImage(file);
      setForm((f) => ({ ...f, heroImagePath: res.path, heroImageUrl: null }));
      toast.success("Immagine caricata");
    } catch {
      toast.error("Caricamento non riuscito");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.updateStudio(form);
      onSaved();
      toast.success("Impostazioni salvate");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="flex flex-col gap-3">
      <label className="relative flex h-40 cursor-pointer items-center justify-center overflow-hidden rounded-xl border border-dashed border-mo-line bg-mo-input" data-testid="studio-hero-upload">
        {imageSourceFor({ imagePath: form.heroImagePath, imageUrl: form.heroImageUrl }) ? (
          <img src={imageSourceFor({ imagePath: form.heroImagePath, imageUrl: form.heroImageUrl })} alt="" className="h-full w-full object-cover" />
        ) : (
          <span className="flex flex-col items-center gap-1 text-mo-muted"><Camera className="h-6 w-6" /><span className="font-body text-xs">Immagine hero</span></span>
        )}
        {uploading && <div className="absolute inset-0 flex items-center justify-center bg-black/50"><Loader2 className="h-5 w-5 animate-spin text-mo-brand" /></div>}
        <input type="file" accept="image/*" className="hidden" onChange={uploadHero} />
      </label>
      <Field label="Nome studio" value={form.name} onChange={set("name")} data-testid="studio-name" />
      <Field label="Titolo Hero" value={form.heroTitle} onChange={set("heroTitle")} data-testid="studio-herotitle" />
      <Field label="Sottotitolo Hero" value={form.heroSubtitle} onChange={set("heroSubtitle")} data-testid="studio-herosub" />
      <Field label="Descrizione" textarea value={form.description} onChange={set("description")} data-testid="studio-desc" />
      <Field label="Indirizzo" value={form.address} onChange={set("address")} data-testid="studio-address" />
      <Field label="Telefono" value={form.phone} onChange={set("phone")} data-testid="studio-phone" />
      <Field label="Email" value={form.email} onChange={set("email")} data-testid="studio-email" />
      <Field label="WhatsApp (numero con prefisso)" value={form.whatsapp} onChange={set("whatsapp")} data-testid="studio-whatsapp" />
      <Field label="Standard igienici" textarea value={form.hygiene} onChange={set("hygiene")} data-testid="studio-hygiene" />

      <div className="mt-3 border-t border-mo-line pt-4">
        <SectionLabel>Immagine principale sezione Studio</SectionLabel>
        <UploadTile
          item={{ imagePath: form.studioImagePath, imageUrl: form.studioImageUrl }}
          label="Immagine Studio"
          className="mt-2 h-40 w-full"
          testid="studio-main-upload"
          onUploaded={(path) => setForm((f) => ({ ...f, studioImagePath: path, studioImageUrl: null }))}
        />
      </div>

      <div className="mt-3 border-t border-mo-line pt-4" data-testid="flash-editor">
        <SectionLabel>Flash Tattoo Disponibili</SectionLabel>
        <div className="mt-3 flex flex-col gap-4">
          {(form.flashes || []).map((fl, i) => (
            <div key={i} className="flex gap-3" data-testid={`flash-edit-${i}`}>
              <UploadTile
                item={fl}
                className="h-24 w-24"
                testid={`flash-upload-${i}`}
                onUploaded={(path) => setFlash(i, { imagePath: path, imageUrl: null })}
              />
              <div className="flex flex-1 flex-col gap-2">
                <Field value={fl.title} onChange={(e) => setFlash(i, { title: e.target.value })} placeholder="Titolo" data-testid={`flash-title-${i}`} />
                <Field value={fl.subtitle} onChange={(e) => setFlash(i, { subtitle: e.target.value })} placeholder="Sottotitolo" data-testid={`flash-subtitle-${i}`} />
              </div>
              <button type="button" onClick={() => setForm((f) => ({ ...f, flashes: f.flashes.filter((_, j) => j !== i) }))} data-testid={`flash-remove-${i}`} className="flex h-9 w-9 items-center justify-center self-start rounded-lg hover:bg-mo-surface3">
                <Trash2 className="h-4 w-4 text-mo-error" />
              </button>
            </div>
          ))}
          <GhostButton icon={Plus} onClick={() => setForm((f) => ({ ...f, flashes: [...(f.flashes || []), { title: "", subtitle: "", imagePath: null, imageUrl: null }] }))} data-testid="flash-add">
            Aggiungi flash tattoo
          </GhostButton>
        </div>
      </div>
      <PrimaryButton icon={Check} onClick={save} loading={saving} className="mt-1" data-testid="studio-save">Salva impostazioni</PrimaryButton>
    </Card>
  );
}

/* -------------------- Page -------------------- */
export default function Profilo() {
  const { user, ready, isAdmin } = useAuth();
  if (!ready) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-mo-brand" /></div>;
  if (!user) return <AuthView />;
  if (isAdmin) return <AdminView />;
  return <UserView />;
}
